import type {
  MaterialBalanceFieldPoint,
  MaterialBalanceResult,
} from '@/types/materialBalance';

export type MbFieldSeriesPoint = {
  year: number;
  oil: number;
  liq: number;
  inj: number;
  np: number;
  fw: number;
  n_prod: number;
  n_inj: number;
};

export type MbFieldAggregate = {
  field: MbFieldSeriesPoint[];
  xDomain: [number, number] | null;
  /** Sum of extractable oil (OOIP × RF) across layers that have both values. */
  extractableOilKt: number | null;
  layerCount: number;
  layersWithResult: number;
};

function extractableFromResult(result: MaterialBalanceResult): number | null {
  const ngz = result.initial_geological_kt;
  const rf = result.kvyt_used ?? result.target_recovery_factor ?? null;
  if (ngz == null || rf == null || !(ngz > 0) || !(rf > 0)) return null;
  return ngz * rf;
}

/**
 * Sum applied per-layer MB `last_result.field` into a field-level series.
 * Water cut is recomputed from summed oil/liq (not averaged).
 */
export function aggregateMbFieldResults(
  results: Array<MaterialBalanceResult | null | undefined>,
): MbFieldAggregate {
  const present = results.filter((r): r is MaterialBalanceResult => Boolean(r?.field?.length));
  if (!present.length) {
    return {
      field: [],
      xDomain: null,
      extractableOilKt: null,
      layerCount: results.length,
      layersWithResult: 0,
    };
  }

  const byYear = new Map<
    number,
    { oil: number; liq: number; inj: number; np: number; n_prod: number; n_inj: number }
  >();

  let yearMin = Infinity;
  let yearMax = -Infinity;
  let extractableSum = 0;
  let extractableN = 0;

  for (const res of present) {
    if (res.year0 != null) yearMin = Math.min(yearMin, res.year0);
    if (res.year1 != null) yearMax = Math.max(yearMax, res.year1);
    const ex = extractableFromResult(res);
    if (ex != null) {
      extractableSum += ex;
      extractableN += 1;
    }
    for (const row of res.field || []) {
      const y = row.year;
      yearMin = Math.min(yearMin, y);
      yearMax = Math.max(yearMax, y);
      const cur = byYear.get(y) || { oil: 0, liq: 0, inj: 0, np: 0, n_prod: 0, n_inj: 0 };
      cur.oil += Number(row.oil) || 0;
      cur.liq += Number(row.liq) || 0;
      cur.inj += Number(row.inj) || 0;
      cur.np += Number(row.np) || 0;
      cur.n_prod += Number(row.n_prod) || 0;
      cur.n_inj += Number(row.n_inj) || 0;
      byYear.set(y, cur);
    }
  }

  const years = [...byYear.keys()].sort((a, b) => a - b);
  const field: MbFieldSeriesPoint[] = years.map((year) => {
    const r = byYear.get(year)!;
    const fw = r.liq > 1e-12 ? 100 * (1 - r.oil / r.liq) : 0;
    return {
      year,
      oil: r.oil,
      liq: r.liq,
      inj: r.inj,
      np: r.np,
      fw: Math.min(100, Math.max(0, fw)),
      n_prod: r.n_prod,
      n_inj: r.n_inj,
    };
  });

  const xDomain: [number, number] | null =
    Number.isFinite(yearMin) && Number.isFinite(yearMax) ? [yearMin, yearMax] : null;

  return {
    field,
    xDomain,
    extractableOilKt: extractableN > 0 ? extractableSum : null,
    layerCount: results.length,
    layersWithResult: present.length,
  };
}

/** Map aggregate points to MaterialBalanceFieldPoint shape for chart reuse. */
export function toMbFieldPoints(series: MbFieldSeriesPoint[]): MaterialBalanceFieldPoint[] {
  return series.map((r) => ({
    year: r.year,
    oil: r.oil,
    liq: r.liq,
    inj: r.inj,
    np: r.np,
    fw: r.fw,
    n_prod: r.n_prod,
    n_inj: r.n_inj,
    recovery_factor: 0,
  }));
}
